import { type FC, useMemo } from "react";
import type {
	ClientPortfolioDetailRelatedResponseDTO,
	ClientRelatedOptionsResponseDTO,
	ClientRelatedPinRequestDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import { PinChoiceDrawer } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PinChoiceDrawer";
import { PinExistingDrawer } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PinExistingDrawer";
import { PinExistingOptionRow } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PinExistingOptionRow";
import type {
	PinCandidate,
	RelatedPinKind,
} from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/pinKind";
import { usePinDrawerStep } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/usePinDrawerStep";
import { useRelatedPinCandidates } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/useRelatedPinCandidates";
import {
	relatedByPinKind,
	relatedTitle,
} from "web_myplann/Portfolio/Helpers/relatedByPinKind";

type Step = "choose" | "existing";

type Props = {
	kind: RelatedPinKind;
	open: boolean;
	onClose: () => void;
	related: ClientPortfolioDetailRelatedResponseDTO[];
	options: ClientRelatedOptionsResponseDTO | undefined;
	isLoading: boolean;
	isPending: boolean;
	onPin: (request: ClientRelatedPinRequestDTO) => void;
	onUnpin: (relatedId: number) => void;
};

export const RelatedPinDrawerContent: FC<Props> = ({
	kind,
	open,
	onClose,
	related,
	options,
	isLoading,
	isPending,
	onPin,
	onUnpin,
}) => {
	const { t } = useResource();
	const { step, setStep } = usePinDrawerStep<Step>(open, "choose");

	const candidates = useRelatedPinCandidates(options, kind);

	const pinnedItems = useMemo(
		() =>
			relatedByPinKind(related, kind).map((item) => ({
				id: item.id,
				title: relatedTitle(item, kind, t),
				canUnpin: item.canUnpin,
			})),
		[kind, related, t],
	);

	const selectCandidate = (candidate: PinCandidate) => {
		if (candidate.kind !== "related") {
			return;
		}

		onPin(
			kind === "property"
				? { targetPropertyId: candidate.data.myplannId }
				: { targetPortfolioId: candidate.data.myplannId },
		);
	};

	return (
		<>
			<PinChoiceDrawer
				kind={kind}
				open={open && step === "choose"}
				onClose={onClose}
				pinnedItems={pinnedItems}
				isPending={isPending}
				onUnpin={onUnpin}
			>
				<PinExistingOptionRow
					kind={kind}
					isLast
					onClick={() => setStep("existing")}
				/>
			</PinChoiceDrawer>
			<PinExistingDrawer
				kind={kind}
				open={open && step === "existing"}
				onClose={onClose}
				candidates={candidates}
				isLoading={isLoading}
				isPending={isPending}
				onSelect={selectCandidate}
			/>
		</>
	);
};
